class ShipCountdown extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('script[type="application/json"][data-sc-config]');
    this.textEl = this.querySelector('[data-sc-text]');
    if (!configEl || !this.textEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }

    if (!this.config.enabled) return;

    this.onVariantChanged = (event) => {
      const variant = event && event.detail ? event.detail.variant : null;
      if (!variant) return;
      this.render(this.variantOptions(variant));
    };
    document.addEventListener('variant:changed', this.onVariantChanged);

    this.refresh = () => {
      if (!document.hidden) this.render(this.lastOptions || []);
    };
    document.addEventListener('visibilitychange', this.refresh);
    this.refreshTimer = setInterval(this.refresh, 60000);

    this.render(this.config.initialOptions || []);
  }

  disconnectedCallback() {
    if (this.onVariantChanged) document.removeEventListener('variant:changed', this.onVariantChanged);
    if (this.refresh) document.removeEventListener('visibilitychange', this.refresh);
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  variantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
  }

  normalize(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[“”″]/g, '"')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();
  }

  toList(value) {
    const result = [];
    String(value == null ? '' : value)
      .split(/[\n,]+/)
      .forEach((part) => {
        const trimmed = part.trim();
        if (trimmed) result.push(trimmed);
      });
    return result;
  }

  isAllowedProduct() {
    const handles = this.toList(this.config.limitHandles).map((handle) => handle.toLowerCase());
    if (!handles.length) return true;
    return handles.indexOf(String(this.config.productHandle || '').toLowerCase()) !== -1;
  }

  matchesVariant(options, matchValues) {
    const present = (options || []).map((value) => this.normalize(value));
    const groups = String(matchValues == null ? '' : matchValues)
      .split(/\n\s*\n/)
      .map((groupText) =>
        groupText
          .split('\n')
          .map((value) => this.normalize(value))
          .filter(Boolean)
      )
      .filter((group) => group.length);
    if (!groups.length) return false;
    return groups.some((group) => group.every((value) => present.indexOf(value) !== -1));
  }

  chicagoToday() {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = {};
    for (const part of fmt.formatToParts(new Date())) {
      parts[part.type] = part.value;
    }
    return {
      year: parseInt(parts.year, 10),
      month: parseInt(parts.month, 10),
      day: parseInt(parts.day, 10),
    };
  }

  countdownNumber(returnDate, startNumber) {
    const raw = returnDate;
    if (!raw) return null;
    const parts = String(raw).split('-');
    if (parts.length !== 3) return null;

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;

    let today;
    try {
      today = this.chicagoToday();
    } catch (error) {
      return null;
    }
    if (!today.year || !today.month || !today.day) return null;

    const returnUTC = Date.UTC(year, month - 1, day);
    const todayUTC = Date.UTC(today.year, today.month - 1, today.day);
    const daysUntil = Math.round((returnUTC - todayUTC) / 86400000);
    if (daysUntil <= 0) return null;

    let floor = Number(this.config.floorNumber);
    if (!isFinite(floor)) floor = 3;

    let start = Number(startNumber);
    if (!isFinite(start)) start = floor;

    let number = floor + (daysUntil - 1);
    if (number > start) number = start;
    if (number < floor) return null;

    return number;
  }

  setText(value) {
    if (this.textEl) this.textEl.textContent = value;
  }

  timelines() {
    const list = Array.isArray(this.config.timelines) ? this.config.timelines : [];
    // Back-compat with the legacy single-timeline config shape.
    if (!list.length && (this.config.matchValues != null || this.config.returnDate != null)) {
      return [{ matchValues: this.config.matchValues, returnDate: this.config.returnDate, startNumber: this.config.startNumber }];
    }
    return list;
  }

  render(options) {
    options = options || [];
    this.lastOptions = options;
    const evergreen = this.config.evergreenText || '';

    if (!this.isAllowedProduct()) {
      this.setText(evergreen);
      return;
    }

    // Evaluate each restock group in order; the first matching variant group
    // with a valid future date wins.
    let number = null;
    const timelines = this.timelines();
    for (let i = 0; i < timelines.length; i++) {
      const timeline = timelines[i];
      if (!timeline || !this.matchesVariant(options, timeline.matchValues)) continue;
      const candidate = this.countdownNumber(timeline.returnDate, timeline.startNumber);
      if (candidate !== null) {
        number = candidate;
        break;
      }
    }

    if (number === null) {
      this.setText(evergreen);
      return;
    }

    // When a range spread is configured, show the number as a range (e.g. "11-14")
    // where the computed number is the high end and the low end is that many days less.
    let display = String(number);
    let isRange = false;
    const spread = Number(this.config.rangeSpread);
    if (isFinite(spread) && spread > 0) {
      let low = number - spread;
      if (low < 1) low = 1;
      if (low < number) {
        display = low + '-' + number;
        isRange = true;
      }
    }

    let text = String(this.config.countdownText || '').replace('{n}', display);
    if (!isRange && number === 1) {
      text = text.replace(/\bdays\b/gi, (match) => (match.charAt(0) === 'D' ? 'Day' : 'day'));
    }
    this.setText(text);
  }
}

if (!customElements.get('ship-countdown')) {
  customElements.define('ship-countdown', ShipCountdown);
}
